/**
 * Trick play: which cards you are allowed to play, and who takes the trick.
 */

import type { Card, HouseRules, Trick, TrumpSuit } from '@shared/types/game';

/**
 * Cards that may legally be played.
 *
 * Leading: anything. Otherwise you must follow the led suit if you hold it.
 * If you are void, everything is allowed — unless the `mustPlayTrumpIfVoid`
 * house rule is on, in which case you must trump when you can.
 */
export function legalPlays(
  hand: readonly Card[],
  trick: Trick,
  trumpSuit: TrumpSuit,
  houseRules: HouseRules
): Card[] {
  if (trick.length === 0) return [...hand];

  const ledSuit = trick[0].card.suit;
  const canFollow = hand.filter((card) => card.suit === ledSuit);
  if (canFollow.length > 0) return canFollow;

  if (houseRules.mustPlayTrumpIfVoid && trumpSuit !== null) {
    const trumps = hand.filter((card) => card.suit === trumpSuit);
    if (trumps.length > 0) return trumps;
  }

  return [...hand];
}

export function isLegalPlay(
  card: Card,
  hand: readonly Card[],
  trick: Trick,
  trumpSuit: TrumpSuit,
  houseRules: HouseRules
): boolean {
  return legalPlays(hand, trick, trumpSuit, houseRules).some(
    (legal) => legal.suit === card.suit && legal.rank === card.rank
  );
}

/**
 * Seat that wins the trick: the highest trump if any were played, otherwise the
 * highest card of the led suit. Cards of other suits can never win, which is
 * why discarding is safe.
 */
export function trickWinner(trick: Trick, trumpSuit: TrumpSuit): number {
  if (trick.length === 0) {
    throw new Error('cannot determine the winner of an empty trick');
  }

  const ledSuit = trick[0].card.suit;
  const trumps = trumpSuit === null ? [] : trick.filter((played) => played.card.suit === trumpSuit);
  const contenders = trumps.length > 0 ? trumps : trick.filter((p) => p.card.suit === ledSuit);

  return contenders.reduce((best, played) => (played.card.rank > best.card.rank ? played : best))
    .seat;
}

/** Suit that was led, or null for a trick nobody has played to yet. */
export function ledSuit(trick: Trick): TrumpSuit {
  return trick.length > 0 ? trick[0].card.suit : null;
}

/** Seat to play next, given who led and how many cards are down. */
export function seatToPlay(leaderSeat: number, trick: Trick, playerCount: number): number {
  return (leaderSeat + trick.length) % playerCount;
}
